import { TrendingUp, TrendingDown } from "lucide-react";

const C = { surface: "var(--bc-panel, #09090B)", highlight: "var(--bc-panel-elev, #141416)", border: "var(--bc-border, rgba(255,255,255,0.07))", text: "var(--bc-ink, #FFFFFF)", muted: "var(--bc-sub, #71717A)", green: "#22C55E", red: "#EF4444", yellow: "#F59E0B", cyan: "#06B6D4" };

const MARKETS = [
  { key: "home", label: "1 (Vittoria Casa)", combKey: "home" },
  { key: "draw", label: "X (Pareggio)", combKey: "draw" },
  { key: "away", label: "2 (Vittoria Ospite)", combKey: "away" },
  { key: "over25", label: "Over 2.5", combKey: "over25" },
  { key: "under25", label: "Under 2.5", combKey: "under25" },
  { key: "btts_yes", label: "Goal (BTTS Sì)", combKey: "btts_yes" },
  { key: "btts_no", label: "No Goal (BTTS No)", combKey: "btts_no" },
];

function ValueBadge({ value, book, model }) {
  // value > 0 = bookmaker pays MORE than fair odds → positive value
  const edge = book && model ? ((book - model) / model * 100) : (value || 0);
  if (Math.abs(edge) < 3) return null;
  const positive = edge > 0;
  return (
    <span
      className="inline-flex items-center gap-0.5 font-data text-[10px] font-bold px-1.5 py-0.5"
      style={{
        background: positive ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.1)",
        color: positive ? C.green : C.red,
        border: `1px solid ${positive ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.2)"}`,
      }}
    >
      {positive ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
      {positive ? "+" : ""}{edge.toFixed(1)}%
    </span>
  );
}

export default function OddsPanel({ oddsData, valueData, combined }) {
  if (!oddsData || Object.keys(oddsData).length === 0) return null;

  // Compute model (fair) odds from combined probabilities
  const modelOdds = (combKey) => {
    const prob = combined?.[combKey];
    if (!prob || prob <= 0) return null;
    return (100 / Math.min(prob, 99)).toFixed(2);
  };

  const bookOdds = (key) => oddsData[key] || null;
  const valueEdge = (key) => valueData?.[key] || null;

  const rows = MARKETS.filter((m) => bookOdds(m.key) || modelOdds(m.combKey));
  if (rows.length === 0) return null;

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="odds-panel">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
        <TrendingUp size={13} style={{ color: C.cyan }} />
        <span className="font-heading font-bold text-xs uppercase tracking-widest" style={{ color: C.text }}>Quote</span>
        <span className="ml-auto font-data text-[10px] uppercase tracking-wider" style={{ color: C.muted }}>
          Comparazione Bookmaker / Modello
        </span>
      </div>

      {/* Column labels */}
      <div className="grid px-4 py-2" style={{ gridTemplateColumns: "1fr 90px 90px 70px", borderBottom: `1px solid ${C.border}` }}>
        <span className="font-data text-[10px] uppercase tracking-wider" style={{ color: C.muted }}>Mercato</span>
        <span className="font-data text-[10px] uppercase tracking-wider text-right" style={{ color: C.muted }}>Book</span>
        <span className="font-data text-[10px] uppercase tracking-wider text-right" style={{ color: C.cyan }}>Modello</span>
        <span className="font-data text-[10px] uppercase tracking-wider text-right" style={{ color: C.muted }}>Edge</span>
      </div>

      {/* Rows */}
      {rows.map((m) => {
        const book = bookOdds(m.key);
        const model = modelOdds(m.combKey);
        const edge = valueEdge(m.key);
        const bookNum = book ? parseFloat(book) : null;
        const modelNum = model ? parseFloat(model) : null;

        // Value: bookmaker odds > model odds = positive edge (book underestimates the event)
        const hasValue = bookNum && modelNum && bookNum > modelNum;
        const edgePct = bookNum && modelNum ? ((bookNum - modelNum) / modelNum * 100) : null;
        const significant = edgePct && Math.abs(edgePct) >= 3;

        return (
          <div
            key={m.key}
            className="grid px-4 py-2.5 items-center transition-colors"
            style={{
              gridTemplateColumns: "1fr 90px 90px 70px",
              borderBottom: `1px solid ${C.highlight}`,
              background: hasValue && significant ? "rgba(34,197,94,0.04)" : "transparent",
            }}
            data-testid={`odds-row-${m.key}`}
          >
            {/* Market name */}
            <span className="font-data text-xs" style={{ color: hasValue && significant ? C.green : C.muted }}>
              {m.label}
            </span>

            {/* Quota Book */}
            <div className="text-right">
              {book ? (
                <span className="font-data text-sm font-bold tabular-nums" style={{ color: C.text }}>
                  {typeof book === "number" ? book.toFixed(2) : book}
                </span>
              ) : (
                <span className="font-data text-xs" style={{ color: C.highlight }}>—</span>
              )}
            </div>

            {/* Quota Modello (fair value) */}
            <div className="text-right">
              {model ? (
                <span className="font-data text-sm font-bold tabular-nums" style={{ color: modelNum < bookNum ? C.green : modelNum > bookNum ? C.red : C.cyan }}>
                  {model}
                </span>
              ) : (
                <span className="font-data text-xs" style={{ color: C.highlight }}>—</span>
              )}
            </div>

            {/* Edge */}
            <div className="text-right flex justify-end">
              {significant ? (
                <ValueBadge value={edgePct} book={bookNum} model={modelNum} />
              ) : (
                <span className="font-data text-xs" style={{ color: C.highlight }}>—</span>
              )}
            </div>
          </div>
        );
      })}

      {/* Summary note */}
      <div className="px-4 py-2" style={{ borderTop: `1px solid ${C.highlight}` }}>
        <p className="font-data text-[10px]" style={{ color: C.muted }}>
          <span style={{ color: C.cyan }}>Quota Modello</span> = quota equa secondo il nostro algoritmo.
          {" "}Edge{" "}
          <span style={{ color: C.green }}>verde</span> = bookmaker sottostima l'evento (valore statistico positivo).
        </p>
      </div>
    </div>
  );
}
